import { useEffect, useState } from 'react';
import { Button, Dialog, Field, Icon, Input, useErrorDialog, useToast } from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';
import { day, daysUntil, type BrandRow, type BrandWriteResult } from './types.ts';

/**
 * Renew a licence.
 *
 * THE THING THIS SCREEN EXISTS FOR. `licence_expires_at` had exactly one writer
 * in the whole repository — `packages/db/src/seed.ts` — so the dashboard's
 * 60-day advance warning counted down to a date nobody on the platform could
 * move. An operator could see the warning, could not act on it, and the warning
 * would eventually turn into "lapsed" with no path back.
 *
 * Its own dialog rather than a field on the edit form, because it is its own
 * ACT: it asserts that a regulator has renewed something, it is the one change
 * on this screen that moves a platform-wide warning, and it is the change
 * somebody will later search the audit trail for by name
 * (`BRAND_LICENCE_RENEWED`).
 */
export function RenewLicenceDialog({
  brand,
  onClose,
  onDone,
}: {
  brand: BrandRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [licenceRef, setLicenceRef] = useState('');
  const [expires, setExpires] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /* Seeded from the brand each time one is chosen. A renewal usually keeps the
     same reference and moves only the date, and re-typing a regulator's
     reference from memory is how a digit gets dropped. */
  useEffect(() => {
    setLicenceRef(brand?.licence_ref ?? '');
    setExpires('');
    setFields({});
  }, [brand]);

  const close = () => {
    setFields({});
    onClose();
  };

  const submit = async () => {
    if (!brand) return;
    const problems: Record<string, string> = {};
    if (expires === '') {
      problems['licenceExpiresAt'] = 'Give the date the renewed licence runs to.';
    } else if (new Date(`${expires}T00:00:00+08:00`).getTime() <= Date.now()) {
      problems['licenceExpiresAt'] =
        'That date has already passed, and would show this brand as lapsed on every dashboard.';
    }
    if (licenceRef.trim() !== '' && licenceRef.trim().length < 3) {
      problems['licenceRef'] = 'A licence reference is at least 3 characters.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setBusy(true);
    try {
      /* Midnight Manila, sent as an instant — a licence expires at a
         regulator's moment, and the date control can only offer a day. Anchored
         to the gaming day's clock, never to the browser's. */
      const result = await api.patch<BrandWriteResult>(`/api/platform/brands/${brand.id}`, {
        licenceExpiresAt: new Date(`${expires}T00:00:00+08:00`).toISOString(),
        ...(licenceRef.trim() && licenceRef.trim() !== (brand.licence_ref ?? '')
          ? { licenceRef: licenceRef.trim() }
          : {}),
      });
      toast(
        `${result.ref} licence recorded to ${day(result.licenceExpiresAt)} — the dashboard warning follows it.`,
      );
      onDone();
      close();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not record the renewal',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const remaining = brand?.licence_expires_at ? daysUntil(brand.licence_expires_at) : null;

  return (
    <Dialog
      open={brand !== null}
      onClose={close}
      title={brand ? `Renew ${brand.name}'s licence` : 'Renew licence'}
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Record the renewal
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="certificate" />
          <div>
            {brand?.licence_expires_at ? (
              <>
                <span className="tabular">{brand.licence_ref ?? 'No reference recorded'}</span>{' '}
                currently runs to <span className="tabular">{day(brand.licence_expires_at)}</span>
                {remaining !== null
                  ? remaining < 0
                    ? ` — ${Math.abs(remaining)} days ago, so this brand reads as lapsed.`
                    : ` — ${remaining} days from now.`
                  : '.'}
              </>
            ) : (
              <>
                No expiry is recorded for this brand, so the dashboard cannot warn about it at all.
                Recording one starts the 60-day advance warning.
              </>
            )}
          </div>
        </div>

        <Field
          label="Licence reference"
          error={fields['licenceRef']}
          hint="Carried over from the current licence. Change it only if the regulator issued a new reference."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={licenceRef}
              onChange={(e) => setLicenceRef(e.target.value)}
              placeholder="PAGCOR-2026-0417"
            />
          )}
        </Field>

        <Field
          label="Runs to (PHT)"
          error={fields['licenceExpiresAt']}
          hint="Philippine time, the clock every date on this screen is printed in."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="date"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
            />
          )}
        </Field>

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            This records what the regulator granted; it does not apply for anything. The dashboard&rsquo;s
            60-day warning reads the date directly, so it moves the moment this is saved, and the
            change is written to the audit trail as a renewal in the same transaction.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
